import { useEffect, useState, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import FilterBar from "./FilterBar";
import KpiRow from "./KpiRow";
import FunnelCards from "./FunnelCards";
import FunnelDonut from "./FunnelDonut";
import TrendChart from "./TrendChart";
import StagesTable from "./StagesTable";
import AddressesTable from "./AddressesTable";
import ManageStagesModal from "./ManageStagesModal";
import { fetchStages, createStage, updateStage, deleteStage, fetchFunnel, fetchTrend, fetchStageDuration } from "./api";
import { nyPeriodRange, nyCustomRange } from "./nyTime";
import { readFiltersFromUrl, writeFiltersToUrl } from "./urlFilters";

export default function LeadsTrackingDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [stages, setStages] = useState([]);
  const [funnel, setFunnel] = useState({ stages: [] });
  const [trend, setTrend] = useState([]);
  const [stageDuration, setStageDuration] = useState({ total_addresses: 0, average_seconds: 0, addresses: [] });
  const [stageDurationLoading, setStageDurationLoading] = useState(false);
  const [fromStageId, setFromStageId] = useState(null);
  const [toStageId, setToStageId] = useState(null);

  // Initial filter state comes from the URL (if it has valid params),
  // so a hard refresh -- or sharing the link -- restores the same view
  // instead of always resetting to "monthly". Read once per mount.
  const initialFilters = readFiltersFromUrl();

  const [period, setPeriod] = useState(initialFilters.period);
  const [customRange, setCustomRange] = useState(initialFilters.customRange);

  const [showManageStages, setShowManageStages] = useState(false);

  const isCustomRange = !!(customRange.from || customRange.to);

  const loadStages = useCallback(async () => {
    try {
      const data = await fetchStages();
      setStages(data);
      if (data.length) {
        const first = data[0];
        const last = data[data.length - 1];
        setFromStageId((current) => (current && data.some((s) => Number(s.id) === Number(current)) ? current : first.id));
        setToStageId((current) => (current && data.some((s) => Number(s.id) === Number(current)) ? current : last.id));
      }
    } catch (err) {
      console.error("Failed to load stages:", err);
      setError(err.message || "Failed to load pipeline stages");
    }
  }, []);

  const loadStageDuration = useCallback(async () => {
    if (!fromStageId || !toStageId) return;
    const fromStage = stages.find((s) => Number(s.id) === Number(fromStageId));
    const toStage = stages.find((s) => Number(s.id) === Number(toStageId));
    if (!fromStage || !toStage || Number(fromStage.position) >= Number(toStage.position)) return;

    setStageDurationLoading(true);
    try {
      const range = isCustomRange ? nyCustomRange(customRange.from, customRange.to) : nyPeriodRange(period);
      const data = await fetchStageDuration({
        from_stage_id: fromStageId,
        to_stage_id: toStageId,
        from_date: range.from,
        to_date: range.to,
      });
      setStageDuration(data);
    } catch (err) {
      console.error("Failed to load stage duration:", err);
      setError(err.message || "Failed to load stage duration");
    } finally {
      setStageDurationLoading(false);
    }
  }, [fromStageId, toStageId, stages, period, customRange, isCustomRange]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = isCustomRange ? nyCustomRange(customRange.from, customRange.to) : nyPeriodRange(period);
      const filters = { from_date: range.from, to_date: range.to };

      const [funnelData, trendData] = await Promise.all([fetchFunnel(filters), fetchTrend({ ...filters, group_by: "day" })]);

      setFunnel(funnelData);
      setTrend(trendData);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [period, customRange, isCustomRange]);

  useEffect(() => {
    loadStages();
  }, [loadStages]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    loadStageDuration();
  }, [loadStageDuration]);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setCustomRange({ from: "", to: "" });
    writeFiltersToUrl({ period: p, customRange: { from: "", to: "" } });
  };

  const handleCustomRangeChange = (next) => {
    setCustomRange(next);
    writeFiltersToUrl({ period, customRange: next });
  };

  const handleAddStage = async (name) => {
    try {
      await createStage(name);
      await loadStages();
      await loadDashboardData();
    } catch (err) {
      console.error("Failed to add stage:", err);
      setError(err.message || "Failed to add stage");
    }
  };

  const handleRenameStage = async (stageId, name) => {
    try {
      await updateStage(stageId, { name });
      await loadStages();
      await loadDashboardData();
    } catch (err) {
      console.error("Failed to rename stage:", err);
      setError(err.message || "Failed to rename stage");
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      await deleteStage(stageId);
      await loadStages();
      await loadDashboardData();
    } catch (err) {
      console.error("Failed to delete stage:", err);
      setError(err.message || "Failed to delete stage");
    }
  };

  const handleReorderStages = async (reordered) => {
    const previous = stages;
    setStages(reordered);
    try {
      await Promise.all(reordered.map((s, index) => updateStage(s.id, { position: index + 1 })));
      await loadDashboardData();
    } catch (err) {
      console.error("Failed to reorder stages:", err);
      setError(err.message || "Failed to reorder stages");
      setStages(previous);
    }
  };

  const funnelStages = loading ? [] : funnel.stages;

  return (
    <div className="min-h-screen bg-gray-100 p-1 sm:p-6 font-sans">
      <div className="max-w-[1600px] mx-auto">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg px-3 py-2 mb-4">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        )}

        <FilterBar period={isCustomRange ? null : period} onPeriodChange={handlePeriodChange} from={customRange.from} to={customRange.to} onCustomRangeChange={handleCustomRangeChange} onManageStages={() => setShowManageStages(true)} />

        <KpiRow
          stages={funnelStages}
          durationStages={stages}
          fromStageId={fromStageId}
          toStageId={toStageId}
          onFromChange={(id) => {
            setFromStageId(id);
            const selected = stages.find((s) => Number(s.id) === Number(id));
            if (selected && toStageId) {
              const currentTo = stages.find((s) => Number(s.id) === Number(toStageId));
              if (!currentTo || Number(currentTo.position) <= Number(selected.position)) {
                const next = stages.find((s) => Number(s.position) > Number(selected.position));
                if (next) setToStageId(next.id);
              }
            }
          }}
          onToChange={setToStageId}
          stageDuration={stageDuration}
          stageDurationLoading={stageDurationLoading}
        />

        <FunnelCards stages={funnelStages} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <FunnelDonut stages={funnelStages} />
          <TrendChart title="Leads added over time" subtitle="New addresses entering the pipeline, per day" data={trend} />
        </div>

        <StagesTable stages={funnelStages} />
        {/* <AddressesTable stages={stages} /> */}
      </div>

      <ManageStagesModal isOpen={showManageStages} stages={stages} onClose={() => setShowManageStages(false)} onAdd={handleAddStage} onRename={handleRenameStage} onDelete={handleDeleteStage} onReorder={handleReorderStages} />
    </div>
  );
}
